/**
 * Safely create a URL from a string that may be absolute or relative.
 * Uses window.location.origin when available (browser), otherwise example.com.
 */
export const toUrl = (input: string, baseFallback = 'https://example.com'): URL => {
  const base = typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : baseFallback
  return new URL(input, base)
}

/** True if the URL's pathname equals /join-zuto (with or without trailing slash). */
export const isJoinZutoPathname = (input: string): boolean => {
  try {
    const url = toUrl(input)
    const pathnameLower = url.pathname.trim().toLowerCase()
    return pathnameLower === '/join-zuto' || pathnameLower === '/join-zuto/'
  } catch {
    return false
  }
}

/** Add or replace a single query parameter on a string URL, returning a string URL. */
export const setQueryParam = (input: string, key: string, value: string): string => {
  const url = toUrl(input)
  url.searchParams.set(key, value)
  return url.toString()
}

/**
 * If the redirect is heading to /join-zuto (either directly or via an inner nextUrl),
 * add or replace the `vehicle` query parameter with the provided vehicle type.
 *
 * - Preserves all other query params.
 * - Mutates the *inner* nextUrl if present and pointing to /join-zuto.
 * - Otherwise mutates the outer URL if it points to /join-zuto.
 */
export const addVehicleToJoinZutoRedirect = (redirectUrl: string, vehicleType: string): string => {
  const normalizedVehicle = (vehicleType || '').trim().toLowerCase()
  if (!normalizedVehicle) return redirectUrl

  const outerUrl = toUrl(redirectUrl)
  const nextUrlParam = outerUrl.searchParams.get('nextUrl')

  if (nextUrlParam) {
    // Inner (pre-auth) path
    const decodedInner = decodeURIComponent(nextUrlParam)
    if (isJoinZutoPathname(decodedInner)) {
      const updatedInner = setQueryParam(decodedInner, 'vehicle', normalizedVehicle)
      outerUrl.searchParams.set('nextUrl', updatedInner)
      return outerUrl.toString()
    }
    return outerUrl.toString()
  }

  // Direct path
  if (isJoinZutoPathname(outerUrl.toString())) {
    outerUrl.searchParams.set('vehicle', normalizedVehicle)
  }
  return outerUrl.toString()
}

/**
 * Build a redirect URL with an optional `preapproved=true` flag.
 *
 * Behaviour:
 * - Returns `undefined` if `resp` is falsy, `success` is not truthy, or `redirectUrl` is missing.
 * - If `redirectUrl` is a valid absolute URL and contains a `nextUrl` query param:
 *   - `nextUrl` is decoded, optionally decorated with `preapproved=true` (when `isPreapproved` is true),
 *     then re-encoded and put back into the main URL.
 *   - If `nextUrl` already has query params, `&preapproved=true` is appended.
 *   - Otherwise, `?preapproved=true` is appended.
 * - If there is no `nextUrl` param and `isPreapproved` is true:
 *   - `preapproved=true` is added directly to the main URL's query params.
 * - If `redirectUrl` is not a valid absolute URL:
 *   - The original `resp.redirectUrl` string is returned unchanged.
 *
 * @param resp - The response containing a redirect URL and a success flag.
 * @param resp.success - Indicates whether the response is successful.
 * @param resp.redirectUrl - The URL to redirect to.
 * @param isPreapproved - Whether the current user/session is pre-approved.
 * @returns The updated redirect URL string, or `undefined` if no redirect should occur.
 */
export function buildRedirectUrl(
  resp: { success?: boolean; redirectUrl?: string } | undefined,
  isPreapproved: boolean
): string | undefined {
  if (!resp?.success || !resp.redirectUrl) return undefined
  try {
    const redirectUrl = new URL(resp.redirectUrl)
    const nextUrl = redirectUrl.searchParams.get('nextUrl')

    if (nextUrl) {
      // pre-auth path (new users, join-zuto)
      const decodedNextUrl = decodeURIComponent(nextUrl)
      const updatedNextUrl = isPreapproved
        ? (decodedNextUrl.includes('?')
            ? `${decodedNextUrl}&preapproved=true`
            : `${decodedNextUrl}?preapproved=true`)
        : decodedNextUrl
      redirectUrl.searchParams.set('nextUrl', updatedNextUrl)
    } else if (isPreapproved) {
      // sign-in path (no nextUrl)
      redirectUrl.searchParams.append('preapproved', 'true')
    }
    
    return redirectUrl.toString()
  } catch {
    // If resp.redirectUrl isn’t a valid absolute URL, fall back to original
    return resp.redirectUrl
  }
}

